/**
 * Strike (Foundry: rule-element/strike.ts): an attack the Entry gives the Character, from a fist
 * to a battle form's jaws. The Rule Element registers a weapon constructor; the strike pipeline
 * builds the weapon with the handwraps' runes for unarmed attacks and prepares the strike.
 */
import { abpLevelOf } from "../inventory";
import { applyItemAlterationsTo } from "./item-alteration";
import { localize } from "../labels";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import { sluggify } from "../slug";
import type { EngineState } from "../state";
import { WEAPON_CATEGORIES, isAttribute, isDamageDieSize, isDamageType, type DamageDieSize } from "../values";
import { EngineWeapon, weaponEntry, type WeaponRunes } from "../weapons";
import type { RuleHandler, RuleInstance } from "./base";

export interface StrikeData {
  slug: string;
  label: string;
  category: string;
  group: string | null;
  baseType: string | null;
  traits: string[];
  traitToggles: { modular: number | null; versatile: string | null };
  handsHeld: 1 | 2;
  otherTags: string[];
  range: { increment: number | null; max: number | null } | null;
  damage: { damageType: string; dice: unknown; die: DamageDieSize; modifier: number };
  img: string;
  attackModifier: number | null;
  replaceAll: boolean;
  replaceBasicUnarmed: boolean;
  battleForm: boolean;
  ability: string | null;
  options: string[];
  fist: boolean;
  graspingAppendage: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parse(rule: RuleInstance, state: EngineState): void {
  const s = rule.source;
  const fist = s.fist === true;
  const label = fist ? localize(state, "PF2E.Weapon.Base.fist", "Fist") : (state.catalogue?.localize(rule.label) ?? rule.label);
  rule.label = label;
  const category = fist ? "unarmed" : typeof s.category === "string" && s.category ? s.category : "unarmed";
  if (!(WEAPON_CATEGORIES as readonly string[]).includes(category)) return rule.fail(`category: "${category}" is not a weapon category`);
  const traits = fist ? ["agile", "finesse", "nonlethal"] : Array.isArray(s.traits) ? s.traits.filter((t): t is string => typeof t === "string" && !!t) : [];
  const toggles = isPlainObject(s.traitToggles) ? s.traitToggles : {};
  const range = isPlainObject(s.range) ? { increment: typeof s.range.increment === "number" ? s.range.increment : s.range.increment === null ? null : 5, max: typeof s.range.max === "number" ? s.range.max : null } : null;
  const rawDamage = isPlainObject(s.damage) && isPlainObject(s.damage.base) ? s.damage.base : {};
  const die = fist ? "d4" : rawDamage.die === undefined ? "d4" : rawDamage.die;
  if (!isDamageDieSize(die)) return rule.fail(`damage.base.die: "${String(die)}" is not a damage die`);
  const handsHeld = s.handsHeld === 2 ? 2 : 1;
  const data: StrikeData = {
    slug: fist ? "fist" : (rule.slug ?? sluggify(label)),
    label,
    category,
    group: fist ? "brawling" : typeof s.group === "string" && s.group ? s.group : null,
    baseType: fist ? "fist" : typeof s.baseType === "string" && s.baseType ? s.baseType : null,
    traits,
    traitToggles: { modular: typeof toggles.modular === "number" ? toggles.modular : null, versatile: typeof toggles.versatile === "string" ? toggles.versatile : null },
    handsHeld,
    otherTags: fist ? [] : Array.isArray(s.otherTags) ? s.otherTags.filter((t): t is string => typeof t === "string") : [],
    range: fist ? null : range,
    damage: fist
      ? { damageType: "bludgeoning", dice: 1, die: "d4", modifier: 0 }
      : { damageType: typeof rawDamage.damageType === "string" && rawDamage.damageType ? rawDamage.damageType : "bludgeoning", dice: rawDamage.dice === undefined ? 1 : cleanRuleValue(rawDamage.dice), die, modifier: typeof rawDamage.modifier === "number" ? rawDamage.modifier : 0 },
    img: typeof s.img === "string" ? s.img : "",
    attackModifier: typeof s.attackModifier === "number" ? s.attackModifier : null,
    replaceAll: !fist && s.replaceAll === true,
    replaceBasicUnarmed: !fist && s.replaceBasicUnarmed === true,
    battleForm: !fist && s.battleForm === true,
    ability: typeof s.ability === "string" && s.ability ? s.ability : null,
    options: Array.isArray(s.options) ? s.options.filter((o): o is string => typeof o === "string") : [],
    fist,
    graspingAppendage: ["fist", "claw"].includes(fist ? "fist" : String(s.baseType ?? "")) ? s.graspingAppendage !== false : category === "unarmed" || traits.includes("unarmed") ? s.graspingAppendage === true : false,
  };
  if (fist) rule.priority = 99;
  rule.slug = data.slug;
  rule.data = data as unknown as Record<string, unknown>;
}

/** Build the weapon (Foundry: StrikeRuleElement#constructWeapon). Null when a value fails to resolve. */
export function constructStrikeWeapon(rule: RuleInstance, state: EngineState, unarmedRunes: WeaponRunes | undefined): EngineWeapon | null {
  const data = rule.data as unknown as StrikeData;
  const attribute = data.ability ? resolveInjectedProperties(rule, state, data.ability) || null : null;
  if (attribute !== null && !isAttribute(attribute)) {
    rule.fail(`Unrecognized attribute: ${attribute}`);
    return null;
  }
  const damageType = resolveInjectedProperties(rule, state, data.damage.damageType);
  if (!isDamageType(damageType)) {
    rule.fail(`Unrecognized damage type: ${damageType}`);
    return null;
  }
  const dice = Math.min(12, Math.max(0, Math.floor(Number(resolveValue(rule, state, data.damage.dice)))));
  if (Number.isNaN(dice)) {
    rule.fail("dice does not resolve to a number");
    return null;
  }
  const group = data.group ? resolveInjectedProperties(rule, state, data.group) : null;
  const groups = state.catalogue?.table("weaponGroups");
  if (group !== null && groups && !(group in groups)) {
    rule.fail(`Unrecognized weapon group: ${group}`);
    return null;
  }
  const id = data.fist ? "xxxxxxFISTxxxxxx" : rule.item.id;
  const system: Record<string, unknown> = {
    slug: data.slug,
    description: { value: "" },
    category: data.category,
    group,
    baseItem: data.baseType,
    attribute,
    bonus: { value: 0 },
    damage: { damageType, dice, die: data.damage.die, modifier: data.damage.modifier },
    range: data.range?.increment ?? null,
    maxRange: data.range?.max ?? null,
    traits: { value: [...data.traits], otherTags: [...data.otherTags], rarity: "common", toggles: { modular: { selected: data.traitToggles.modular }, versatile: { selected: data.traitToggles.versatile } } },
    options: { value: [...data.options] },
    runes: data.category === "unarmed" ? { potency: unarmedRunes?.potency ?? 0, striking: unarmedRunes?.striking ?? 0, property: [...(unarmedRunes?.property ?? [])] } : { potency: 0, striking: 0, property: [] },
    usage: { value: "held-in-one-hand" },
    equipped: { carryType: "held", handsHeld: data.handsHeld },
    graspingAppendage: data.graspingAppendage,
    level: { value: 0 },
  };
  const weapon = new EngineWeapon({
    entry: weaponEntry(id, data.label, data.slug, system, `${rule.item.uuid}#${rule.index}`),
    rule,
    battleForm: data.battleForm,
    fist: data.fist,
    fixedAttack: data.attackModifier,
    carryType: "held",
    handsHeld: data.handsHeld,
    grantedBy: rule.item.grantedBy,
    abpLevel: abpLevelOf(state),
  });
  weapon.name = data.label;
  applyItemAlterationsTo(state, weapon);
  return weapon;
}

function beforePrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  if (!rule.test(state)) {
    rule.ignored = true;
    return;
  }
  const data = rule.data as unknown as StrikeData;
  state.synthetics.strikes[data.slug] = (unarmedRunes) => constructStrikeWeapon(rule, state, unarmedRunes);
}

/** Exclude other strikes if this Rule Element says so (Foundry: replaceAll / replaceBasicUnarmed). */
function afterPrepareData(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as StrikeData;
  if (data.replaceAll) {
    state.strikes = state.strikes.filter((s) => s.weapon.rule?.item === rule.item && s.weapon.name === data.label && s.weapon.group === (data.group ? resolveInjectedProperties(rule, state, data.group, { warn: false }) : null));
  } else if (data.replaceBasicUnarmed) {
    state.strikes = state.strikes.filter((s) => s.slug !== "basic-unarmed");
  }
}

export const StrikeHandler: RuleHandler = { key: "Strike", parse, beforePrepareData, afterPrepareData };
